/**
 * 工程表の紙（PDF）を画面の中で見る・印刷する・保存する（工程管理の5回目、#104、docs/39 §7.2 の9。見積書の紙の画面＝R13 と同じ形）
 *   /groups/:id/schedule/paper?paper=&from=&to=&include=  … 案件の工程表
 *   /schedule/paper?mode=&paper=&from=&to=&q=&open=&include= … 全体の工程表
 *   PDF は API（GET …/export?format=pdf）が Chromium で作る。ここは pdf.js で描いて紙として並べ、上に「PDF 保存」「印刷する」。
 *   戻るのは題の左上の「‹ 工程表」（#98 の戻るボタン。読み上げは「工程表に戻る」）。印刷はこの画面の紙だけ（body.paper-mode）
 */
import { SCHEDULE_PAPERS, SchedulePaperKey, scheduleExportFileName, type GroupDetail, type ScheduleView } from "@genbaryoku/contract";
import { SCHEDULE_EXPORT_MSG as X } from "./msg.js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { productHeaders } from "../../lib/product.js";
import { t } from "../../labels.js";
import { renderPdf, type PageImg } from "../Paper.jsx";
import { todayLocal } from "./timeline.js";

export function SchedulePaperPage({ kind }: { kind: "group" | "overview" | "standalone" }) {
  const { id = "", sid = "" } = useParams();
  const [sp] = useSearchParams();
  // 案件なしの{schedule}（#291）は /schedules/:sid/paper
  const loose = kind === "standalone";
  const group = kind === "group" || loose;
  const back = useBackNav({ to: loose ? `/schedules/${sid}` : group ? `/groups/${id}/schedule` : "/schedule", label: t("{schedule}") });
  usePageName(X.paperTitle);
  const g = useQuery({ queryKey: ["group", id], queryFn: () => api<GroupDetail>("GET", `/groups/${id}`), enabled: kind === "group", retry: false });
  const sq = useQuery({ queryKey: ["schedule", "s", sid], queryFn: () => api<ScheduleView>("GET", `/schedules/${sid}`), enabled: loose, retry: false });
  const name = loose ? (sq.data?.schedule?.title ?? "") : (g.data?.name ?? "");
  const paperKey = SchedulePaperKey.catch("a3l").parse(sp.get("paper"));
  const paper = SCHEDULE_PAPERS[paperKey];
  const mode = sp.get("mode") === "vendor" ? "vendor" : "site";
  const qs = useMemo(() => {
    const q = new URLSearchParams(sp);
    q.set("format", "pdf");
    return q.toString();
  }, [sp]);
  const pdfUrl = loose ? `/api/v1/schedules/${sid}/export?${qs}` : group ? `/api/v1/groups/${id}/schedule/export?${qs}` : `/api/v1/schedule/overview/export?${qs}`;
  const today = useMemo(() => todayLocal(), []);
  const subject = group ? name : t(mode === "vendor" ? "{contractor}別" : "{siteShort}別");
  const fileName = scheduleExportFileName(group ? { group: name } : { overview: mode }, today, "pdf", t);

  const [pages, setPages] = useState<PageImg[]>([]);
  const [total, setTotal] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  // タブの名前：「工程表：工事名」
  useEffect(() => {
    const prev = document.title;
    document.title = `${X.titleGroup}：${subject}`;
    return () => {
      document.title = prev;
    };
  }, [subject]);

  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setPages([]);
    setTotal(0);
    setError(undefined);
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(pdfUrl, { credentials: "include", headers: productHeaders() });
        if (!res.ok) {
          const p = (await res.json().catch(() => null)) as { title?: string } | null;
          throw new Error(res.status === 401 ? "ログインが必要です" : p?.title ? t(p.title) : `PDF を作成できませんでした（${res.status}）`);
        }
        const blob = await res.blob();
        if (!alive) return;
        made = URL.createObjectURL(blob);
        setBlobUrl(made);
        await renderPdf(await blob.arrayBuffer(), (p, i, n) => {
          if (!alive) return;
          setTotal(n);
          setPages((ps) => {
            const next = ps.slice();
            next[i - 1] = p;
            return next;
          });
        });
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : messageOf(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [pdfUrl]);

  const doPrint = () => {
    if (pages.length) window.print();
  };
  // 紙の画面の並び：横の紙は A4 縦（820px）と同じ縮尺で広く
  const pageStyle = { width: `min(100%, ${Math.round((paper.w / 210) * 820)}px)` };

  return (
    <div className="paperview" data-testid="sch-paper">
      <style>{`@page { size: ${paperKey === "a3l" ? "A3" : "A4"} ${paper.landscape ? "landscape" : "portrait"}; margin: 0; }`}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        <h1>{X.titleGroup}</h1>
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {subject}　{paper.label}
        </span>
        <span className="sp" />
        <a className={"btn" + (blobUrl ? "" : " disabled")} href={blobUrl ?? undefined} download={fileName} aria-disabled={!blobUrl} data-testid="sch-paper-save">
          {X.save}
        </a>
        <button type="button" className="btn primary" onClick={doPrint} disabled={loading || pages.length === 0 || !!error} data-testid="sch-paper-print">
          {X.print}
        </button>
      </div>
      <ErrorText>{error}</ErrorText>
      {loading && pages.length === 0 && !error ? <p className="hint">{X.loading}</p> : null}
      <div className="pages" aria-label={X.paperTitle}>
        {pages.map((p, i) =>
          p ? (
            <div className="paperpage" key={i} style={pageStyle} data-testid="sch-paper-page">
              <img src={p.url} alt={`${X.titleGroup} ${i + 1}ページ目${total ? `／${total}` : ""}`} width={p.w} height={p.h} />
            </div>
          ) : null,
        )}
      </div>
      {total > 0 ? (
        <p className="hint" style={{ textAlign: "center" }} data-testid="sch-paper-count">
          {`${total}ページ`}
        </p>
      ) : null}
      {back.dialog}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {pages.map((p, i) =>
            p ? (
              <div className="printedPage" key={i}>
                <img src={p.url} alt="" width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
